import type {
  CodingAnswerOption,
  ObservationComponentSpec,
  ObservationTemplate,
} from '@seen/core/codings'
import { featureFlags } from '@seen/core/featureFlags'
import type { ExtendedObservation } from '@seen/core/models/observation'
import {
  type ComponentValue,
  componentLine,
  componentPattern,
  componentValue,
  formatCodes,
  unitLabel,
  withComponentValue,
} from '@seen/core/models/observationComponents'
import {
  isCalculatedComponent,
  withDerivedComponents,
} from '@seen/core/observation-logic/derivations/components'
import { Button } from '@seen/ui'
import { IconChevronDown } from '@tabler/icons-react'
import { type JSX, useState } from 'react'
import { toast } from 'sonner'
import { ObservationAnswerPicker } from '#src/components/rich-text-editor/ObservationNode/ObservationAnswerPicker.tsx'
import { answerChipClass } from '#src/components/rich-text-editor/ObservationNode/observationChip.ts'
import { useNodeRecord } from '#src/components/rich-text-editor/ObservationNode/useNodeRecord.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'

export interface ObservationComponentsEditorProps {
  observation: ExtendedObservation
  template: ObservationTemplate
  disabled?: boolean
}

// Every part of a record kept as components, in the details: numbers for the
// quantities, answers for the coded parts. Each change writes only its own
// part. A part the typed line names also changes the line, so the note and
// the record agree.
export function ObservationComponentsEditor({
  observation,
  template,
  disabled,
}: ObservationComponentsEditorProps): JSX.Element {
  const practitioner = useCurrentPractitioner()
  const record = useNodeRecord()
  const derivedObservations = useFeatureFlag(featureFlags.structuredCapture)

  const setPart = (
    spec: ObservationComponentSpec,
    value: ComponentValue | undefined
  ): void => {
    record
      .patch((o) => {
        const set = withComponentValue(template, o.component, spec, value)
        // A part entered by hand is kept as entered; the parts computed from
        // the others follow them.
        const components = derivedObservations
          ? withDerivedComponents(template, set)
          : set
        o.component = components.length ? components : undefined
        if (formatCodes(template).includes(spec.coding.code ?? '')) {
          const line = typedLine(template, o)
          if (line !== undefined) {
            o.setNoteFirst(line, practitioner.reference)
          }
        }
      }, observation)
      .catch((cause: unknown) => {
        toast.error(
          `Failed to change ${spec.label}${cause instanceof Error ? `: ${cause.message}` : ''}`
        )
      })
  }

  return (
    <div className="grid grid-cols-[auto_1fr] items-center gap-x-3 gap-y-2 text-sm">
      {template.components.map((spec) => {
        const value = componentValue(observation, spec.coding)
        const entry = observation.component?.find((component) =>
          component.code?.coding?.some(
            (coding) => coding.code === spec.coding.code
          )
        )
        return (
          <ComponentRow
            key={spec.coding.code}
            spec={spec}
            value={value}
            calculated={
              derivedObservations && !!entry && isCalculatedComponent(entry)
            }
            disabled={disabled}
            onChange={(next) => setPart(spec, next)}
          />
        )
      })}
    </div>
  )
}

// The typed line with the parts it names rewritten from the record, or
// undefined when the line has no such text to rewrite.
function typedLine(
  template: ObservationTemplate,
  observation: ExtendedObservation
): string | undefined {
  const pattern = componentPattern(template)
  const filled = componentLine(template, observation)
  if (!pattern || filled === undefined) {
    return undefined
  }
  const current = observation.noteFirstText ?? ''
  return pattern.test(current) ? current.replace(pattern, filled) : filled
}

function ComponentRow({
  spec,
  value,
  calculated = false,
  disabled,
  onChange,
}: {
  spec: ObservationComponentSpec
  // Filled in from the other parts rather than entered.
  calculated?: boolean
  value: number | string | CodingAnswerOption | undefined
  disabled?: boolean
  onChange: (value: ComponentValue | undefined) => void
}): JSX.Element {
  const id = `component-${spec.coding.code}`
  return (
    <>
      <label htmlFor={id} className="text-foreground-secondary">
        {spec.label}
      </label>
      {spec.scaleType === 'Ord' || spec.scaleType === 'Nom' ? (
        <ObservationAnswerPicker
          answerOptions={spec.answerOptions ?? []}
          answerValueSet={null}
          scaleType={spec.scaleType}
          codingDisplay={spec.label}
          value={typeof value === 'object' ? value : null}
          onSelect={onChange}
          disabled={disabled}
          sideBySide
        >
          {/* Opens the list when the answers do not fit side by side. */}
          <Button
            id={id}
            variant="unstyled"
            size="unstyled"
            disabled={disabled}
            className={answerChipClass()}
          >
            {typeof value === 'object' ? value.display : 'Select…'}
            <IconChevronDown className="size-3" />
          </Button>
        </ObservationAnswerPicker>
      ) : (
        <span className="flex items-baseline gap-2">
          <NumberPart
            // Starts again from a value saved elsewhere.
            key={String(value)}
            id={id}
            spec={spec}
            value={typeof value === 'object' ? undefined : value}
            disabled={disabled}
            onChange={onChange}
          />
          {calculated ? (
            <span className="text-xs text-foreground-secondary">
              calculated
            </span>
          ) : null}
        </span>
      )}
    </>
  )
}

// Saved when the field is left, so a half-typed number is never written.
function NumberPart({
  id,
  spec,
  value,
  disabled,
  onChange,
}: {
  id: string
  spec: ObservationComponentSpec
  value: number | string | undefined
  disabled?: boolean
  onChange: (value: number | undefined) => void
}): JSX.Element {
  const [draft, setDraft] = useState(value === undefined ? '' : String(value))
  return (
    <span className="flex items-baseline gap-1">
      <input
        id={id}
        type="number"
        value={draft}
        disabled={disabled}
        className="w-[8ch] rounded-[calc(var(--radius)-5px)] pl-1 placeholder:text-placeholder focus-visible:ring-2 focus-visible:ring-zinc-950 focus-visible:outline-hidden"
        placeholder="Value"
        onChange={(event) => setDraft(event.target.value)}
        onBlur={() => {
          const next = draft.trim() === '' ? undefined : Number(draft)
          if (next !== undefined && Number.isNaN(next)) {
            return
          }
          if (next !== (value === undefined ? undefined : Number(value))) {
            onChange(next)
          }
        }}
      />
      {spec.unit && (
        <span className="text-foreground-secondary">
          {unitLabel(spec.unit)}
        </span>
      )}
    </span>
  )
}
